import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiTool, FiShield, FiAward, FiUsers, FiTrendingUp, FiArrowRight, FiCheckCircle } = FiIcons;

const HomePage = () => {
  const capabilities = [
    {
      icon: FiSettings,
      title: 'Heavy Engineering & Fabrication',
      description: 'Large-scale fabrication projects for mining and infrastructure applications'
    },
    {
      icon: FiTool,
      title: 'Precision Machining & CNC Operations',
      description: 'State-of-the-art CNC machining with tolerances to ±0.005mm'
    },
    {
      icon: FiShield,
      title: 'Industrial Automation & Control Systems',
      description: 'Advanced automation solutions for modern manufacturing'
    },
    {
      icon: FiAward,
      title: 'Quality Assurance & Testing Services',
      description: 'Comprehensive testing protocols and quality management'
    },
    {
      icon: FiUsers,
      title: 'Custom Component Manufacturing',
      description: 'Tailored solutions for unique industrial requirements'
    },
    {
      icon: FiTrendingUp,
      title: 'Maintenance & Refurbishment Services',
      description: 'Expert maintenance and restoration of industrial equipment'
    }
  ];

  const stats = [
    { number: '40+', label: 'Years of Excellence' },
    { number: '15', label: 'Hectares of Facilities' },
    { number: '500+', label: 'Projects Completed' },
    { number: '99%', label: 'Client Satisfaction' }
  ];

  const clients = [
    'Global Giant',
    'Oil Refinery Group',
    'Metal Industries',
    'All Metal Mines',
    'Power Power',
    'Transworld Industries'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Precision Manufacturing
              <span className="block text-orange-500">Excellence Since 1985</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto text-gray-200">
              Gauteng Manufacturing Co. represents four decades of South African industrial excellence, 
              producing world-class components and assemblies for mining, automotive, and infrastructure sectors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <span>Our Services</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                Get Quote
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 opacity-20">
          <SafeIcon icon={FiSettings} className="text-6xl animate-spin" style={{ animationDuration: '20s', color: '#f2b994' }} />
        </div>
        <div className="absolute bottom-20 right-10 opacity-20">
          <SafeIcon icon={FiTool} className="text-4xl" style={{ color: '#f2b994' }} />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-blue-800 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Capabilities */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Manufacturing Capabilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From our state-of-the-art facilities in Germiston and Vanderbijlpark, we deliver 
              precision-engineered solutions that power South Africa's industrial economy.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <SafeIcon icon={capability.icon} className="text-2xl text-blue-800" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{capability.title}</h3>
                <p className="text-gray-600">{capability.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Four Decades of Industrial Excellence
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Our commitment to quality, safety, and continuous improvement has established 
                long-standing partnerships with major mining houses, automotive manufacturers, 
                and infrastructure developers throughout Southern Africa.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl" />
                  <span className="text-gray-700">ISO 9001:2015 Certified</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl" />
                  <span className="text-gray-700">SANS Compliant Operations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl" />
                  <span className="text-gray-700">Advanced CNC Machining Centers</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl" />
                  <span className="text-gray-700">Comprehensive Quality Assurance</span>
                </div>
              </div>
              <Link
                to="/about"
                className="bg-blue-800 hover:bg-blue-900 text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
              >
                <span>Learn More</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-100 to-orange-100 rounded-2xl p-8 h-96 flex items-center justify-center">
                <div className="text-center">
                  <SafeIcon icon={FiSettings} className="text-8xl text-blue-800 mb-4 mx-auto" />
                  <p className="text-gray-600 font-medium">State-of-the-Art Manufacturing Facility</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted Clients */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold mb-8">Trusted by Industry Leaders</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {clients.map((client, index) => (
                <div
                  key={index}
                  className="bg-gray-800 rounded-lg p-6 flex items-center justify-center hover:bg-gray-700 transition-colors"
                >
                  <span className="font-semibold text-center">{client}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Start Your Project?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Let our experienced team help you with precision manufacturing solutions 
              tailored to your specific requirements.
            </p>
            <Link
              to="/contact"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2 text-lg"
            >
              <span>Get Your Quote Today</span>
              <SafeIcon icon={FiArrowRight} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;